import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { DivineServiceTypeDto } from '../../models/divine-service.model';

/**
 * Servicio que centraliza toda la lógica de validación para Servicios Divinos
 */
@Injectable({
  providedIn: 'root'
})
export class DivineServiceValidationService {
  constructor(private readonly dateAdapter: DateAdapter<any>) {}

  /**
   * Validador para verificar que un valor sea un GUID válido
   */
  getGuidValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Dejar que Required se encargue
      }

      const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!guidPattern.test(control.value)) {
        return { invalidGuid: true };
      }

      return null;
    };
  }

  /**
   * Validador personalizado: endAt debe ser mayor que startAt
   */
  getEndAtGreaterThanStartAtValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const startAt = group.get('startAt')?.value;
      const startTime = group.get('startTime')?.value;
      const endAt = group.get('endAt')?.value;
      const endTime = group.get('endTime')?.value;

      if (startAt && startTime && endAt && endTime) {
        const start = this.combineDateAndTime(startAt, startTime);
        const end = this.combineDateAndTime(endAt, endTime);
        const startMs = new Date(start).getTime();
        const endMs = new Date(end).getTime();
        if (endMs <= startMs) {
          return { endAtLessThanStartAt: true };
        }
      }

      return null;
    };
  }

  /**
   * Combina una fecha y una hora en formato ISO
   */
  private combineDateAndTime(dateValue: any, timeValue: string): string {
    if (!dateValue || !timeValue) {
      return '';
    }

    const [hours, minutes] = timeValue.split(':');
    const date = new Date(
      this.dateAdapter.getYear(dateValue),
      this.dateAdapter.getMonth(dateValue),
      this.dateAdapter.getDate(dateValue),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      0,
      0
    );

    return date.toISOString();
  }

  /**
   * Convierte una fecha local a una cadena ISO en UTC a medianoche (00:00:00.000Z)
   * Esto asegura que la fecha se envíe correctamente sin desfase de zona horaria
   */
  getDateAtMidnightUTC(dateValue: any): string {
    if (!dateValue) {
      return '';
    }

    const year = this.dateAdapter.getYear(dateValue);
    const month = this.dateAdapter.getMonth(dateValue) + 1;
    const day = this.dateAdapter.getDate(dateValue);

    // Crear una nueva fecha ISO string a medianoche UTC
    // formato: YYYY-MM-DDTHH:MM:SS.sssZ
    const isoString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00.000Z`;

    return isoString;
  }

  /**
   * Actualiza los validadores de los campos de ofrendas según si el tipo de servicio requiere reportar ofrendas
   */
  updateOfferingValidators(
    formGroup: FormGroup,
    selectedTypeId: number,
    serviceTypes: DivineServiceTypeDto[]
  ): void {
    const selectedType = serviceTypes.find(t => t.value === selectedTypeId);
    const offeringRequired = selectedType?.offeringRequired ?? false;

    const offeringsARSControl = formGroup.get('offeringsARS');
    const offeringsUSDControl = formGroup.get('offeringsUSD');

    if (offeringRequired) {
      // Hacer los campos requeridos
      offeringsARSControl?.setValidators([]);
      offeringsUSDControl?.setValidators([]);
    } else {
      // Hacer los campos opcionales (solo min)
      offeringsARSControl?.setValidators([]);
      offeringsUSDControl?.setValidators([]);
    }

    // Actualizar el estado de validación
    offeringsARSControl?.updateValueAndValidity({ emitEvent: false });
    offeringsUSDControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Valida el formulario completo y marca campos como touched si hay errores
   */
  validateFormAndShowErrors(formGroup: FormGroup): boolean {
    if (!formGroup.valid) {
      Object.keys(formGroup.controls).forEach(key => {
        formGroup.get(key)?.markAsTouched();
      });
      return false;
    }
    return true;
  }

  /**
   * Valida que la unidad organizacional sea válida
   */
  validateOrganizationUnit(formGroup: FormGroup): boolean {
    const formValue = formGroup.value;

    if (!formValue.organizationUnitId || formValue.organizationUnitId.trim() === '') {
      formGroup.get('organizationUnitId')?.markAsTouched();
      return false;
    }

    return true;
  }

  /**
   * Valida que las ofrendas sean correctas según el tipo de servicio
   */
  validateOfferings(
    formGroup: FormGroup,
    formValue: any,
    serviceTypes: DivineServiceTypeDto[]
  ): boolean {
    const selectedType = serviceTypes.find(t => t.value === formValue.type);
    if (selectedType?.offeringRequired) {
      const offeringsARS = formValue.offeringsARS;
      const offeringsUSD = formValue.offeringsUSD;

      // Si las ofrendas son requeridas, al menos una debe ser mayor que 0
      if (
        (offeringsARS === null || offeringsARS === undefined || offeringsARS === 0) &&
        (offeringsUSD === null || offeringsUSD === undefined || offeringsUSD === 0)
      ) {
        // Marcar los campos como touched para mostrar errores
        formGroup.get('offeringsARS')?.markAsTouched();
        formGroup.get('offeringsUSD')?.markAsTouched();
        return false;
      }
    }

    return true;
  }
}
